import { get, isEmpty } from 'lodash';
import { FetchExpenseCompsParams, ExpenseCompsResponse, FilterValues } from './types';
import { fetchExpenseComps as fetchExpenseCompsFromCompPlex } from 'utils/comp-plex/api';
import { ExpenseComp } from 'types/underwriting/income-approach/expenses';

const removeEmptyFilters = (filters: FilterValues): FilterValues => {
  const cleanedFilters: FilterValues = {};

  Object.keys(filters).forEach((key) => {
    const obj = filters[key];
    const cleanedObj: { min?: number; max?: number } = {};

    if (obj.min !== undefined && obj.min !== undefined) {
      cleanedObj.min = obj.min;
    }
    if (obj.max !== undefined && obj.max !== undefined) {
      cleanedObj.max = obj.max;
    }

    if (Object.keys(cleanedObj).length > 0) {
      cleanedFilters[key] = cleanedObj;
    }
  });

  return cleanedFilters;
};

const mapToMongoFields = (field: string): string => {
  switch (field) {
    case 'address':
      return 'streetAddress';
    case 'grossBuildingArea':
      return 'grossBuildingArea';
    case 'expenseYear':
      return 'expenseYear';
    case 'totalOperatingExpenses':
    case 'totalOperatingExpenses.sf':
      return 'totalPerSqft';
    case 'totalOperatingExpenses.unit':
    case 'totalOperatingExpensesPerUnit':
      return 'totalPerUnit';
    case 'residentialUnits':
      return 'numberOfResidentialUnits';
    case 'type':
      return 'buildingType';
    default:
      return field;
  }
};

const mapExpenseItems = (items: any) => {
  const expenses: any = {};
  for (const category in items) {
    const total = get(items, `${category}.total`, undefined);
    const mappedExpenseItem = {
      total: total,
      psf: get(items, `${category}.sf`, undefined),
      perUnit: get(items, `${category}.unit`, undefined),
      reported: get(items, `${category}.reported`, !!total),
    };
    expenses[category] = mappedExpenseItem;
  }

  return expenses;
};

const mapFetchedExpenseComps = (expenseComps: any[]): ExpenseComp[] => {
  return expenseComps.map((expenseComp) => {
    const _id = get(expenseComp, '_id', undefined);
    const key = get(expenseComp, 'key', _id);
    const yearBuilt = get(expenseComp, 'yearBuilt', undefined);
    const residentialUnits = get(expenseComp, 'numberOfResidentialUnits', undefined);
    const grossBuildingArea = get(expenseComp, 'grossBuildingArea', undefined);
    const items = get(expenseComp, 'expenses.0', undefined);
    const expensePeriod = get(expenseComp, 'expensePeriod');
    const expenseYear = get(expenseComp, 'expenseYear', undefined);
    const expenseMonth = get(expenseComp, 'expenseMonth', undefined);
    const expenseDate = get(expenseComp, 'expenseDate', undefined);

    const expensesTotalGross = get(expenseComp, 'expensesTotalGross', {});
    const expensesTotalNet = get(expenseComp, 'expensesTotalNet', {});

    const address = expenseComp.streetAddress;
    const city = expenseComp.city;
    const state = expenseComp.state;
    const expenses = mapExpenseItems(items);
    const commercialUnits = get(expenseComp, 'numberOfCommercialUnits');
    const buildingType = get(expenseComp, 'buildingType');
    const sourceOfInformation = get(expenseComp, 'sourceOfInformation', undefined);
    const sourceName = get(expenseComp, 'sourceName', undefined);
    const sourceUrl = get(expenseComp, 'sourceUrl', undefined);
    const expenseHistoryReportId = get(expenseComp, 'expenseHistoryReportId', undefined);
    const approved = get(expenseComp, 'isApproved', undefined);
    const updatedAt = get(expenseComp, 'updatedAt', undefined);
    const createdAt = get(expenseComp, 'createdAt', undefined);

    return {
      _id,
      key,
      boweryId: _id,
      location: city,
      city,
      address,
      yearBuilt,
      residentialUnits,
      commercialUnits,
      grossBuildingArea,
      sourceOfInformation,
      sourceName,
      sourceUrl,
      expenseHistoryReportId,
      approved,
      updatedAt,
      createdAt,
      expenses,
      expensesTotalGross,
      expensesTotalNet,
      expenseYear,
      expenseMonth,
      expensePeriod,
      expenseDate,
      buildingType,
      state,
      propertyType: get(expenseComp, 'buildingType'),
      effectiveGrossIncome: get(expenseComp, 'effectiveGrossIncome'),
      expenseRatio: get(expenseComp, 'expenseRatio'),
      grossRevenue: get(expenseComp, 'grossRevenue'),
      version: get(expenseComp, 'version'),
    };
  });
};

const markSelectedComps = (checkedComps: ExpenseComp[], expenseComps: ExpenseComp[]): ExpenseComp[] => {
  return expenseComps.map((expenseComp) => {
    const isSelected = checkedComps.some(
      (selectedExpenseComp) =>
        selectedExpenseComp._id === expenseComp._id || selectedExpenseComp.boweryId === expenseComp.boweryId
    );
    return {
      ...expenseComp,
      isSelected,
    };
  });
};

export const fetchExpenseComps = async (params: FetchExpenseCompsParams): Promise<ExpenseCompsResponse> => {
  const { checkedComps, tableFilters, options } = params;
  const { pageSize, currentPage, locations, state, compState, compCity, expensePeriod, type } = tableFilters;

  let { sortField, sortDirection } = tableFilters;
  const selectedFilters = removeEmptyFilters(params.filters);

  const mappedLocations = locations.map((location) => {
    return location === 'Manhattan' ? 'New York' : location;
  });

  const filters: any = {
    locations: mappedLocations,
    state,
  };

  sortField = mapToMongoFields(sortField);
  sortDirection = sortDirection.toUpperCase();

  const itemFilters: any[] = [];
  for (const param in selectedFilters) {
    let currentObject;
    switch (param) {
      case 'expenseYear':
        filters['date'] = {
          min: selectedFilters[param].min ? new Date(selectedFilters[param].min!, 0, 1) : undefined,
          max: selectedFilters[param].max ? new Date(selectedFilters[param].max!, 11, 31, 23, 59, 59) : undefined,
        };
        break;
      case 'totalOperatingExpenses':
        filters['totalPerSqft'] = selectedFilters[param];
        break;
      case 'totalOperatingExpensesPerUnit':
        filters['totalPerUnit'] = selectedFilters[param];
        break;
      case 'residentialUnits':
        filters['residentialUnitCount'] = selectedFilters[param];
        break;
      case 'yearBuilt':
        filters['yearBuilt'] = selectedFilters[param];
        break;
      case 'grossBuildingArea':
        filters['grossBuildingArea'] = selectedFilters[param];
        break;
      default:
        currentObject = { key: param, value: selectedFilters[param] };
        itemFilters.push(currentObject);
        break;
    }
  }

  filters.itemFilters = itemFilters;

  if (state) {
    filters.state = state;
  }
  if (compCity) {
    filters.compCity = compCity;
  }
  if (compState && !isEmpty(compState)) {
    filters.compState = compState;
  }
  if (expensePeriod) {
    filters.period = expensePeriod;
  }
  if (type) {
    filters.use = type;
  }

  filters.addedExpenses = get(params, 'comparableExpenses', []);

  try {
    const result = await fetchExpenseCompsFromCompPlex(
      { ...options, ...filters },
      sortField,
      sortDirection,
      pageSize,
      currentPage
    );

    const availableLocations = result.availableLocations.map((location: string) => {
      return location === 'New York' ? 'Manhattan' : location;
    });

    const mappedFetchedExpenseComps = mapFetchedExpenseComps(result.expenseComps);
    const mappedExpenseComps = markSelectedComps(checkedComps, mappedFetchedExpenseComps);

    return {
      ...result,
      availableLocations,
      availableStates: result.availableStates,
      expenseComps: mappedExpenseComps,
    };
  } catch (error) {
    console.error('Error fetching expense comps:', error);
    throw new Error('Failed to fetch expense comparables');
  }
};
